import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { INTERVIEW_STATUS_LABELS, type InterviewSummaryView } from '@nest-recruit/shared';
import { INTERVIEW_STATUS_TONES } from '../../../components/status-tones';
import { ToneChip } from '../../../components/Tone';
import { formatDateTime } from '../../../lib/format';
import { dateTileParts, panelNames, placeLine, roundLine } from '../interview-ui';
import type { InterviewListContext } from '../interview-list-context';
import styles from './Interviews.module.css';

interface InterviewRowProps {
  readonly interview: InterviewSummaryView;
  readonly zone: string;
  readonly action?: ReactNode;
  readonly isJobShown?: boolean;
  /** To schedule and My interviews (Redesign Group 7): the date tile leads the row, the action ends it. */
  readonly hasDateTile?: boolean;
  /** The list this row sits in, so the interview's page can walk it (Previous / Next). */
  readonly listContext?: InterviewListContext;
}

/** One interview in a list — To schedule, My interviews, the phone week, CAND-03's card. */
export function InterviewRow({ interview, zone, action, isJobShown = true, hasDateTile = false, listContext }: InterviewRowProps): ReactNode {
  const body = (
    <>
      <div className={styles.itemHead}>
        <Link to="/interviews/$interviewId" params={{ interviewId: interview.id }} state={listContext ? { interviewList: listContext } : undefined} className={styles.itemName}>
          {interview.candidateName}
        </Link>
        <ToneChip tone={INTERVIEW_STATUS_TONES[interview.status]}>{INTERVIEW_STATUS_LABELS[interview.status]}</ToneChip>
        {interview.isStranded && <ToneChip tone="attention">Left the stage</ToneChip>}
        {action && !hasDateTile && <span className={styles.itemActions}>{action}</span>}
      </div>
      <p className={styles.meta}>
        {interview.startsAt ? formatDateTime(interview.startsAt, { timeZone: zone }) : 'No time yet'} · {roundLine(interview)}
        {isJobShown ? ` · ${interview.jobTitle}` : ''}
      </p>
      <p className={styles.quiet}>
        {placeLine(interview)} · {panelNames(interview)}
      </p>
    </>
  );

  if (!hasDateTile) return <li className={styles.item}>{body}</li>;
  return (
    <li className={styles.row}>
      <DateTile startsAt={interview.startsAt} zone={zone} />
      <div className={styles.rowBody}>{body}</div>
      {action && <span className={styles.rowActions}>{action}</span>}
    </li>
  );
}

/** "Thu / 22 / Oct", or a dashed "No time yet" while the interview waits for one. */
export function DateTile({ startsAt, zone }: { readonly startsAt: string | null; readonly zone: string }): ReactNode {
  if (!startsAt) {
    return (
      <span className={`${styles.dateTile} ${styles.dateTileNone}`} aria-hidden>
        No time yet
      </span>
    );
  }
  const { weekday, day, month } = dateTileParts(startsAt, zone);
  return (
    <span className={styles.dateTile} aria-hidden>
      <span className={styles.dateTileWeekday}>{weekday}</span>
      <span className={styles.dateTileDay}>{day}</span>
      <span className={styles.dateTileMonth}>{month}</span>
    </span>
  );
}
